import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { BarChart3, Eye, MousePointerClick, ShoppingCart, DollarSign, TrendingUp } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Tile = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-black/10 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-black/50"><Icon className="w-4 h-4" /><span className="text-[11px] font-bold uppercase tracking-wider">{label}</span></div>
    <p className="text-2xl font-black font-display mt-2">{value}</p>
  </div>
);

const Board = ({ title, rows, unit = "sales" }) => (
  <div className="border border-black/10 bg-white p-4">
    <p className="text-xs font-bold uppercase tracking-wider text-black/50 mb-2">{title}</p>
    {(!rows || rows.length === 0) ? <p className="text-xs text-muted-foreground">No data yet.</p> : (
      <ol className="space-y-1.5">
        {rows.map((r, i) => (
          <li key={r.name} className="flex items-center justify-between text-sm">
            <span className="truncate"><span className="text-black/40 mr-1.5">{i + 1}.</span>{r.name}</span>
            <span className="font-bold text-taven-orange shrink-0">{r.sales} {unit}</span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("all");
  useEffect(() => { setData(null); adminClient().get(`/admin/analytics/feed?range=${range}`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [range]);
  const t = data && data !== false ? data.totals : null;

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center gap-2 mb-1"><BarChart3 className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">Shop the Feed — Analytics</h1></div>
      <p className="text-sm text-muted-foreground mb-4">Platform-wide live-commerce funnel from stored attribution. Commissions are estimated until confirmed by real TAVEN orders.</p>
      <div className="flex gap-1.5 mb-6" data-testid="admin-range-filter">
        {[["7d", "7 Days"], ["30d", "30 Days"], ["all", "All-time"]].map(([k, l]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`admin-range-${k}`} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition-colors ${range === k ? "bg-taven-black text-white" : "bg-taven-gray/50 text-black/60 hover:text-black"}`}>{l}</button>
        ))}
      </div>
      {data === null ? <p className="text-sm text-muted-foreground">Loading analytics…</p> : data === false ? <p className="text-sm text-muted-foreground">Could not load analytics.</p> : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Tile icon={Eye} label="Video Views" value={t.video_views} testid="an-views" />
        <Tile icon={MousePointerClick} label="Product Clicks" value={t.product_clicks} testid="an-clicks" />
        <Tile icon={ShoppingCart} label="Add-to-Carts" value={t.carts} testid="an-carts" />
        <Tile icon={TrendingUp} label="Confirmed Sales" value={t.confirmed_sales} testid="an-sales" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Tile icon={ShoppingCart} label="Checkout Clicks" value={t.checkout_clicks} testid="an-checkouts" />
        <Tile icon={DollarSign} label="Pending Commissions" value={money(t.pending_commissions)} testid="an-pending" />
        <Tile icon={DollarSign} label="Available Commissions" value={money(t.available_commissions)} testid="an-available" />
        <Tile icon={DollarSign} label="Approved Payouts" value={money(t.approved_payouts)} testid="an-payouts" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Board title="Best Creators" rows={data.best_creators} />
        <Board title="Best Sellers" rows={data.best_sellers} />
        <Board title="Best Products" rows={data.best_products} />
        <Board title="Best Categories" rows={data.best_categories} />
      </div>
      </>
      )}
    </div>
  );
}
